import { Corporation, CorporationService, CorporationRemainingTimesDto } from '@jooxter/api';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback } from 'react';
import { CorporationStaleTimeEnum } from '../staleTimes/corporation.enum';
import { CorporationQueryKeys } from '../queryKeys';

export const useFetchCorporationById = (
  id: number | undefined | null
): { corporation: Corporation | null | undefined; isLoading: boolean } => {
  const fetchCorporation = useCallback(() => {
    if (id) {
      return CorporationService.getById(id);
    }

    return Promise.resolve(null);
  }, [id]);

  const { data: corporation, isLoading } = useQuery({
    queryKey: [CorporationQueryKeys.GetCorporationById, id],
    queryFn: () => fetchCorporation(),
    staleTime: CorporationStaleTimeEnum.GetCorporationById,
  });

  return { corporation, isLoading };
};

export const useFetchCorporationQuota = (
  id: number | undefined | null,
  date: string,
  enabled = true
): { quota: CorporationRemainingTimesDto | null | undefined; isLoading: boolean } => {
  const fetchCorporationQuota = useCallback(() => {
    if (id) {
      return CorporationService.getQuota(id, date);
    }

    return Promise.resolve(null);
  }, [id, date]);

  const { data: quota, isLoading } = useQuery({
    queryKey: [CorporationQueryKeys.GetCorporationQuota, id, date],
    queryFn: () => fetchCorporationQuota(),
    staleTime: CorporationStaleTimeEnum.GetCorporationQuota,
    enabled,
  });

  return { quota, isLoading };
};

export const useInvalidateCorporationQueries = () => {
  const queryClient = useQueryClient();

  const invalidateCorporationQueries = useCallback(() => {
    queryClient.invalidateQueries({
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      predicate: (query: any): boolean => {
        return (
          query.queryKey[0] === CorporationQueryKeys.GetCorporationById ||
          query.queryKey[0] === CorporationQueryKeys.GetCorporationQuota
        );
      },
    });
  }, [queryClient]);

  return { invalidateCorporationQueries };
};
